import { useRequest } from "./request.hooks";
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { UserStateContext } from "@/contexts/UserStateProvider";
import { LiteStatisticPeriod } from "@/types/LiteStatisticPeriod";
import { LiteProductStatistic } from "@/types/LiteProductStatistic";
import moment from "moment";
import { OrdersWithEstimatesResponse, TiktokOrder } from "@/types/TikTokOrder";
import { defaultTaxOptions } from "@/types/TaxConfigurationItem";
import { jsonToBase64 } from "@/lib/base64";
import { DateRangeGroup } from "@/types/DateRangeGroup";
export const LITE_DASHBOARD_STATISTICS = "liteDashboardStatistics";
export const LITE_SAMPLE_STATISTICS = "liteSampleStatistics";
export const LITE_CANCELLED_STATISTICS = "liteCancelledStatistics";
export const LITE_PRODUCT_STATISTICS = "liteProductStatistics";
export const LITE_PRODUCT_SKU_STATISTICS = "liteProductSkuStatistics";
export const CANCELLATION_STATISTICS = "cancellationStatistics";
export const ORDERS_WITH_ESTIMATES = "ordersWithEstimates";

export const useOrdersWithEstimates = ({
    page,
    pageSize,
    dateFrom,
    dateTo,
    statuses,
    productIds,
    skuIds,
    hasPaid,
    isSampleOrder
}: {
    page: number;
    pageSize: number;
    dateFrom?: Date;
    dateTo?: Date;
    statuses?: string[];
    productIds?: string[];
    skuIds?: string[];
    hasPaid?: boolean;
    isSampleOrder?: boolean;
}): { data: OrdersWithEstimatesResponse | undefined, isLoading: boolean, error: Error | null } => {
    const dateFromFormatted = dateFrom ? moment.utc(dateFrom).startOf('day').toISOString() : ""
    const dateToFormatted = dateTo ? moment.utc(dateTo).endOf('day').toISOString() : ""
    const { request } = useRequest();
    const { token, taxRate, taxRateValidFrom, selectedSellerAccountId, cogsCalculation, taxOptions, shippingApplyTo } = useContext(UserStateContext);

    const { data, isLoading, error } = useQuery({
        queryKey: [ORDERS_WITH_ESTIMATES, token, page, pageSize, dateFromFormatted, dateToFormatted, statuses, productIds, skuIds, hasPaid, isSampleOrder, taxRate, taxRateValidFrom, selectedSellerAccountId, cogsCalculation, shippingApplyTo],
        queryFn: () =>
            new Promise((resolve) => {
                setTimeout(() => {
                    resolve(
                        request({
                            url: `/api/v1/tiktokOrders/retrieve/listWithEstimates`,
                            method: "GET",
                            params: {
                                page: page.toString(),
                                pageSize: pageSize.toString(),
                                dateFrom: dateFromFormatted,
                                dateTo: dateToFormatted,
                                defaultTaxRate: taxRate.toString(),
                                taxRateValidFrom: taxRateValidFrom?.toISOString() || "",
                                sellerAccountId: selectedSellerAccountId!.toString(),
                                statuses: statuses?.join(',') || "",
                                productIds: productIds?.join(',') || "",
                                skuIds: skuIds?.join(',') || "",
                                hasPaid: hasPaid?.toString() || "",
                                isSampleOrder: isSampleOrder?.toString() || "",
                                cogsCalculation: cogsCalculation,
                                taxOptions: jsonToBase64(taxOptions || defaultTaxOptions),
                                shippingApplyTo: shippingApplyTo
                            }
                        })
                    );
                }, 500);
            })
                .then((response: any) => {
                    if (response.success) {
                        return response.content;
                    } else {
                        throw new Error(response.errorMessage || "Failed to fetch orders with estimates");
                    }
                }),
        staleTime: 10 * 60 * 1000, // 10 minutes in milliseconds
    });

    return {
        data, isLoading, error
    };
};

export const FULL_TIKTOK_ORDER_BY_ID = "fullTiktokOrderById";

export const useFullTiktokOrderById = ({
    orderId,
}: {
    orderId: string;
}): { data: TiktokOrder | undefined, isLoading: boolean, error: Error | null } => {
    const { request } = useRequest();
    const { token, taxRate, taxRateValidFrom, selectedSellerAccountId, cogsCalculation, taxOptions, shippingApplyTo } = useContext(UserStateContext);

    const { data, isLoading, error } = useQuery({
        queryKey: [FULL_TIKTOK_ORDER_BY_ID, token, orderId, taxRate, taxRateValidFrom, selectedSellerAccountId, cogsCalculation, taxOptions, shippingApplyTo],
        queryFn: () =>
            new Promise((resolve) => {
                setTimeout(() => {
                    resolve(
                        request({
                            url: `/api/v1/tiktokOrders/retrieve/byId/${orderId}`,
                            method: "GET",
                            params: {
                                defaultTaxRate: taxRate.toString(),
                                taxRateValidFrom: taxRateValidFrom?.toISOString() || "",
                                cogsCalculation: cogsCalculation,
                                taxOptions: jsonToBase64(taxOptions || defaultTaxOptions),
                                shippingApplyTo: shippingApplyTo
                            }
                        })
                    );
                }, 500);
            })
                .then((response: any) => {
                    if (response.success) {
                        return response.content;
                    } else {
                        throw new Error(response.errorMessage || "Failed to fetch order by id");
                    }
                }),
        staleTime: 10 * 60 * 1000, // 10 minutes in milliseconds
    });

    return {
        data, isLoading, error
    };
};

export const useLiteDashboardStatistics = (dateFrom: Date, dateTo: Date, productId?: string): { data: LiteStatisticPeriod[], isLoading: boolean, error: Error | null } => {
    const { request } = useRequest();
    const { shouldIncludeAdSpend, token, taxRate, taxRateValidFrom, selectedSellerAccountId, v2, cogsCalculation, taxOptions, shippingApplyTo } = useContext(UserStateContext);

    const { data, isLoading, error } = useQuery({
        queryKey: [LITE_DASHBOARD_STATISTICS, token, dateFrom, dateTo, selectedSellerAccountId, productId, cogsCalculation, taxOptions, shouldIncludeAdSpend, shippingApplyTo],
        queryFn: () =>
            new Promise((resolve) => {
                setTimeout(() => {
                    resolve(
                        request({
                            url: `/api/v1/tiktokOrders/retrieve/liteDashboardStatistics`,
                            method: "GET",
                            params: {
                                dateFrom: dateFrom.toISOString(),
                                dateTo: dateTo.toISOString(),
                                defaultTaxRate: taxRate.toString(),
                                taxRateValidFrom: taxRateValidFrom?.toISOString() || "",
                                sellerAccountId: selectedSellerAccountId!.toString(),
                                productId: productId || "",
                                v2: v2.toString(),
                                shouldIncludeAdSpend: shouldIncludeAdSpend.toString(),
                                cogsCalculation: cogsCalculation,
                                taxOptions: jsonToBase64(taxOptions || defaultTaxOptions),
                                randomMultiplierString: "5.239047838",
                                shippingApplyTo: shippingApplyTo
                            }
                        })
                    );
                }, 500);
            })
                .then((response: any) => {
                    if (response.success) {
                        return response.content;
                    } else {
                        throw new Error(response.errorMessage || "Failed to fetch lite dashboard statistics");
                    }
                }),
        staleTime: 10 * 60 * 1000, // 10 minutes in milliseconds
    });

    return {
        data, isLoading, error
    };
};

export const useLiteSampleStatistics = (dateFrom: Date, dateTo: Date, productId?: string): { data: LiteStatisticPeriod[], isLoading: boolean, error: Error | null } => {
    const { request } = useRequest();
    const { shouldIncludeAdSpend, token, taxRate, taxRateValidFrom, selectedSellerAccountId, v2, cogsCalculation, taxOptions, shippingApplyTo } = useContext(UserStateContext);

    const { data, isLoading, error } = useQuery({
        queryKey: [LITE_SAMPLE_STATISTICS, token, dateFrom, dateTo, selectedSellerAccountId, productId, cogsCalculation, taxOptions, shouldIncludeAdSpend, shippingApplyTo],
        queryFn: () =>
            new Promise((resolve) => {
                setTimeout(() => {
                    resolve(
                        request({
                            url: `/api/v1/tiktokOrders/retrieve/liteSampleStatistics`,
                            method: "GET",
                            params: {
                                dateFrom: dateFrom.toISOString(),
                                dateTo: dateTo.toISOString(),
                                defaultTaxRate: taxRate.toString(),
                                taxRateValidFrom: taxRateValidFrom?.toISOString() || "",
                                sellerAccountId: selectedSellerAccountId!.toString(),
                                productId: productId || "",
                                v2: v2.toString(),
                                shouldIncludeAdSpend: shouldIncludeAdSpend.toString(),
                                cogsCalculation: cogsCalculation,
                                taxOptions: jsonToBase64(taxOptions || defaultTaxOptions),
                                shippingApplyTo: shippingApplyTo
                            }
                        })
                    );
                }, 500);
            })
                .then((response: any) => {
                    if (response.success) {
                        return response.content;
                    } else {
                        throw new Error(response.errorMessage || "Failed to fetch lite dashboard statistics");
                    }
                }),
        staleTime: 10 * 60 * 1000, // 10 minutes in milliseconds
    });

    return {
        data, isLoading, error
    };
};

type LiteDashboardStatistic = {
    periodStart: string;
    periodEnd: string;
    totalUnits: string | null;
    totalTax: number | null;
    costOfGoods: number | null;
    totalPlatformFee: number | null;
    totalMerchandiseSales: number | null;
    totalMerchandiseSalesRefundAmount: number | null;
    totalGrossSales: number | null;
    totalGrossSalesRefundAmount: number | null;
    totalCustomerPayment: number | null;
    totalShippingSalePrice: number | null;
    totalSellerDiscounts: number | null;
    totalPlatformDiscounts: number | null;
    totalEstimatedAffiliateCommission: number | null;
    totalEstimatedFbmShippingCost: number | null;
    totalEstimatedFbtFulfillmentFee: number | null;
    totalEstimatedFbtShippingCost: number | null;
    totalEstimatedFbtFulfilmentAndPackagingFees: number | null;
    totalEstimatedSellerShippingCost: number | null;
    netProfits: number;
    totalCostTaxBack: number;
    totalPlatformFeeTaxBack: number;
    totalAffiliateCommissionTaxBack: number;
    totalEstimatedFbmShippingCostTaxBack: number;
    totalEstimatedFbtFulfillmentFeeTaxBack: number;
    totalEstimatedFbtShippingCostTaxBack: number;
    totalEstimatedSellerShippingCostTaxBack: number;
    totalEstimatedFbtFulfilmentAndPackagingFeesTaxBack: number;
    totalCancelledPlatformDiscounts: number;
    totalCancelledSellerDiscounts: number;
    totalCancelledShippingSalePrice: number;
    totalCancelledCustomerPayment: number;
    totalTaxBack: number;
    units: number;
    cancelledUnits: number;
    systemCancelledUnits: number;
    sellerCancelledUnits: number;
    buyerCancelledUnits: number;
    period: string;
}

type MultipleLiteDashboardStatistics = {
    today?: LiteDashboardStatistic;
    yesterday?: LiteDashboardStatistic;
    this_week?: LiteDashboardStatistic;
    last_week?: LiteDashboardStatistic;
    this_month?: LiteDashboardStatistic;
    last_month?: LiteDashboardStatistic;
    this_year?: LiteDashboardStatistic;
    last_year?: LiteDashboardStatistic;
    this_quarter?: LiteDashboardStatistic;
    last_quarter?: LiteDashboardStatistic;
}

export const useMultipleLiteDashboardStatistics = (types: string[], productId?: string, custom?: {
    dateRange1: {
        current: {
            from: Date | undefined;
            to: Date | undefined;
        };
        previous: {
            from: Date | undefined;
            to: Date | undefined;
        };
    };
    dateRange2: {
        current: {
            from: Date | undefined;
            to: Date | undefined;
        };
        previous: {
            from: Date | undefined;
            to: Date | undefined;
        };
    };
}): { data: MultipleLiteDashboardStatistics | null, isLoading: boolean, error: Error | null } => {
    const { request } = useRequest();
    const { token, taxRate, taxRateValidFrom, selectedSellerAccountId, v2, cogsCalculation, taxOptions, shouldIncludeAdSpend, shippingApplyTo } = useContext(UserStateContext);

    const { data, isLoading, error } = useQuery({
        queryKey: [LITE_DASHBOARD_STATISTICS, token, types, selectedSellerAccountId, productId, cogsCalculation, taxOptions, shouldIncludeAdSpend, custom, shippingApplyTo],
        queryFn: () =>
            new Promise((resolve) => {
                setTimeout(() => {
                    resolve(
                        request({
                            url: `/api/v1/tiktokOrders/retrieve/liteMultipleDashboardStatistics`,
                            method: "GET",
                            params: {
                                types: types.join(','),
                                defaultTaxRate: taxRate.toString(),
                                taxRateValidFrom: taxRateValidFrom?.toISOString() || "",
                                sellerAccountId: selectedSellerAccountId!.toString(),
                                productId: productId || "",
                                v2: v2.toString(),
                                shouldIncludeAdSpend: shouldIncludeAdSpend.toString(),
                                cogsCalculation: cogsCalculation,
                                taxOptions: jsonToBase64(taxOptions || defaultTaxOptions),
                                custom: jsonToBase64(custom ?? null),
                                shippingApplyTo: shippingApplyTo
                            }
                        })
                    );
                }, 500);
            })
                .then((response: any) => {
                    if (response.success) {
                        return response.content;
                    } else {
                        throw new Error(response.errorMessage || "Failed to fetch multiple lite dashboard statistics");
                    }
                }),
        staleTime: 10 * 60 * 1000, // 10 minutes in milliseconds
    });

    return {
        data, isLoading, error
    };
};

export const useLiteCancelledStatistics = (dateFrom: Date, dateTo: Date): { data: any, isLoading: boolean, error: Error | null } => {
    const { request } = useRequest();
    const { token, selectedSellerAccountId, cogsCalculation, shippingApplyTo } = useContext(UserStateContext);

    const { data, isLoading, error } = useQuery({
        queryKey: [LITE_CANCELLED_STATISTICS, token, dateFrom, dateTo, selectedSellerAccountId, cogsCalculation, shippingApplyTo],
        queryFn: () =>
            new Promise((resolve) => {
                setTimeout(() => {
                    resolve(
                        request({
                            url: `/api/v1/tiktokOrders/retrieve/liteCancelledStatistics`,
                            method: "GET",
                            params: {
                                dateFrom: dateFrom.toISOString(),
                                dateTo: dateTo.toISOString(),
                                sellerAccountId: selectedSellerAccountId!.toString(),
                                shippingApplyTo: shippingApplyTo
                            }
                        })
                    );
                }, 500);
            })
                .then((response: any) => {
                    if (response.success) {
                        return response.content;
                    } else {
                        throw new Error(response.errorMessage || "Failed to fetch lite cancelled statistics");
                    }
                }),
        staleTime: 10 * 60 * 1000, // 10 minutes in milliseconds
    });

    return {
        data, isLoading, error
    };
};

export const useCancellationStatistics = (dateFrom: Date, dateTo: Date, productIds: string[], groupBySku?: boolean, retrieveAllForDateRange?: boolean): {
    data: {
        allTimeCancellations: {
            cancelReason: string;
            count: number;
            total: number;
            cancelledCost: number;
            postShipmentCancelledCost: number;
        }[],
        allCancellations: {
            cancelReason: string;
            count: number;
            total: number;
            cancelledCost: number;
            postShipmentCancelledCost: number;
        }[],
        byProductIdCancellations: Record<string, {
            cancelReason: string;
            count: number;
            total: number;
            cancelledCost: number;
            postShipmentCancelledCost: number;
        }[]> | null,
        bySkuCancellations: Record<string, Record<string, {
            cancelReason: string;
            count: number;
            total: number;
            cancelledCost: number;
            postShipmentCancelledCost: number;
        }[]>> | null,
    } | undefined,
    isLoading: boolean,
    error: Error | null
} => {
    const { request } = useRequest();
    const { token, selectedSellerAccountId, cogsCalculation, shippingApplyTo } = useContext(UserStateContext);

    const { data, isLoading, error } = useQuery({
        enabled: (productIds && productIds.length > 0),
        queryKey: [CANCELLATION_STATISTICS, token, dateFrom, dateTo, selectedSellerAccountId, productIds, groupBySku, retrieveAllForDateRange, cogsCalculation, shippingApplyTo],
        queryFn: () =>
            new Promise((resolve) => {
                setTimeout(() => {
                    resolve(
                        request({
                            url: `/api/v1/tiktokOrders/retrieve/cancellationStatistics`,
                            method: "GET",
                            params: {
                                dateFrom: dateFrom.toISOString(),
                                dateTo: dateTo.toISOString(),
                                sellerAccountId: selectedSellerAccountId!.toString(),
                                productIds: productIds.join(','),
                                bySku: groupBySku?.toString() || "false",
                                retrieveAllForDateRange: retrieveAllForDateRange?.toString() || "false",
                                cogsCalculation: cogsCalculation,
                                shippingApplyTo: shippingApplyTo
                            }
                        })
                    );
                }, 500);
            })
                .then((response: any) => {
                    if (response.success) {
                        return response.content;
                    } else {
                        throw new Error(response.errorMessage || "Failed to fetch cancellation statistics");
                    }
                }),
        staleTime: 10 * 60 * 1000, // 10 minutes in milliseconds
    });

    return {
        data, isLoading, error
    };
};

export const useLiteProductStatistics = (dateFrom: Date, dateTo: Date, productIds: string[]): { data: Record<string, LiteStatisticPeriod[]>, isLoading: boolean, error: Error | null } => {
    const { request } = useRequest();
    const { token, taxRate, taxRateValidFrom, selectedSellerAccountId, v2, cogsCalculation, taxOptions, shippingApplyTo } = useContext(UserStateContext);

    const { data, isLoading, error } = useQuery({
        queryKey: [LITE_PRODUCT_STATISTICS, token, dateFrom, dateTo, selectedSellerAccountId, productIds, cogsCalculation, taxOptions, shippingApplyTo],
        enabled: (productIds && productIds.length > 0),
        queryFn: () =>
            new Promise((resolve) => {
                setTimeout(() => {
                    resolve(
                        request({
                            url: `/api/v1/tiktokOrders/retrieve/liteProductStatistics`,
                            method: "GET",
                            params: {
                                dateFrom: dateFrom.toISOString(),
                                dateTo: dateTo.toISOString(),
                                defaultTaxRate: taxRate.toString(),
                                taxRateValidFrom: taxRateValidFrom?.toISOString() || "",
                                sellerAccountId: selectedSellerAccountId!.toString(),
                                productIds: productIds!.join(','),
                                v2: v2.toString(),
                                cogsCalculation: cogsCalculation,
                                taxOptions: jsonToBase64(taxOptions || defaultTaxOptions),
                                shippingApplyTo: shippingApplyTo
                            }
                        })
                    );
                }, 500);
            })
                .then((response: any) => {
                    if (response.success) {
                        return response.content;
                    } else {
                        throw new Error(response.errorMessage || "Failed to fetch lite product statistics");
                    }
                }),
        staleTime: 10 * 60 * 1000, // 10 minutes in milliseconds
    });

    return {
        data, isLoading, error
    };
};

export const useLiteProductSkuStatistics = (dateFrom: Date, dateTo: Date, skuIds: string[]): { data: Record<string, LiteStatisticPeriod[]>, isLoading: boolean, error: Error | null } => {
    const { request } = useRequest();
    const { token, taxRate, taxRateValidFrom, selectedSellerAccountId, v2, cogsCalculation, taxOptions, shippingApplyTo } = useContext(UserStateContext);

    const { data, isLoading, error } = useQuery({
        queryKey: [LITE_PRODUCT_SKU_STATISTICS, token, dateFrom, dateTo, selectedSellerAccountId, skuIds, cogsCalculation, taxOptions, shippingApplyTo],
        enabled: (skuIds && skuIds.length > 0),
        queryFn: () =>
            new Promise((resolve) => {
                setTimeout(() => {
                    resolve(
                        request({
                            url: `/api/v1/tiktokOrders/retrieve/liteProductSkuStatistics`,
                            method: "GET",
                            params: {
                                dateFrom: dateFrom.toISOString(),
                                dateTo: dateTo.toISOString(),
                                defaultTaxRate: taxRate.toString(),
                                taxRateValidFrom: taxRateValidFrom?.toISOString() || "",
                                sellerAccountId: selectedSellerAccountId!.toString(),
                                skuIds: skuIds!.join(','),
                                v2: v2.toString(),
                                cogsCalculation: cogsCalculation,
                                taxOptions: jsonToBase64(taxOptions || defaultTaxOptions),
                                shippingApplyTo: shippingApplyTo
                            }
                        })
                    );
                }, 500);
            })
                .then((response: any) => {
                    if (response.success) {
                        return response.content;
                    } else {
                        throw new Error(response.errorMessage || "Failed to fetch lite product SKU statistics");
                    }
                }),
        staleTime: 10 * 60 * 1000, // 10 minutes in milliseconds
    });

    return {
        data, isLoading, error
    };
};